import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchManagerInfo } from "../api/fplApi";

/**
 * Optional FPL identity for the app.
 * - fplId       : the manager's entry id (string) or null
 * - manager     : { manager_name, team_name } once known, else null
 * - skipped     : true if the user chose "browse without an ID"
 * - decided     : true once the user has either entered an ID or skipped
 *                 (so we never show the landing page again)
 *
 * Nothing here gates the rest of the app — it only *adds* the personal layer.
 */

const FplIdContext = createContext(null);

const LS_ID = "fpl_id";
const LS_SKIP = "fpl_id_skipped";
const LS_MGR = "fpl_manager"; // cached name/team so the header chip is instant

export function FplIdProvider({ children }) {
  const [fplId, setFplIdState] = useState(null);
  const [manager, setManager] = useState(null);
  const [skipped, setSkipped] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load persisted choice on mount
  useEffect(() => {
    try {
      const id = localStorage.getItem(LS_ID);
      const skip = localStorage.getItem(LS_SKIP) === "true";
      const mgr = localStorage.getItem(LS_MGR);
      if (id) setFplIdState(id);
      if (mgr) setManager(JSON.parse(mgr));
      setSkipped(skip);
    } catch (_) {
      /* localStorage blocked — behave as first visit */
    }
    setLoading(false);
  }, []);

  // Validate an id against FPL (returns the manager object or throws)
  const validateId = useCallback(async (rawId) => {
    const id = String(rawId).trim();
    if (!/^\d+$/.test(id)) throw new Error("Please enter a numeric FPL ID.");
    const info = await fetchManagerInfo(id);
    if (!info || !info.manager_name) {
      throw new Error("We couldn't find a team with that ID. Double-check and try again.");
    }
    return { id, info };
  }, []);

  // Save a validated id
  const setFplId = useCallback((id, info) => {
    try {
      localStorage.setItem(LS_ID, String(id));
      localStorage.removeItem(LS_SKIP);
      if (info) localStorage.setItem(LS_MGR, JSON.stringify(info));
    } catch (_) {}
    setFplIdState(String(id));
    setManager(info || null);
    setSkipped(false);
  }, []);

  // Choose to browse without an id (remembered)
  const skip = useCallback(() => {
    try {
      localStorage.setItem(LS_SKIP, "true");
    } catch (_) {}
    setSkipped(true);
  }, []);

  // Remove the id (for "switch team") — drops back to a clean state
  const clearFplId = useCallback(() => {
    try {
      localStorage.removeItem(LS_ID);
      localStorage.removeItem(LS_MGR);
    } catch (_) {}
    setFplIdState(null);
    setManager(null);
  }, []);

  const decided = !!fplId || skipped;

  return (
    <FplIdContext.Provider
      value={{ fplId, manager, skipped, decided, loading, validateId, setFplId, skip, clearFplId }}
    >
      {children}
    </FplIdContext.Provider>
  );
}

export const useFplId = () => {
  const ctx = useContext(FplIdContext);
  if (!ctx) throw new Error("useFplId must be used within <FplIdProvider>");
  return ctx;
};
